import type { DifficultyMetrics } from './types';

export const generatorVersion = 4;

export interface ManifestLevelEntry extends Omit<DifficultyMetrics, 'embeddedTrapCount'> {
  readonly levelNumber: number;
  readonly resolvedSeed: number;
  readonly maskId: string;
  readonly paramsId: string;
  readonly contentHash: string;
}

export type ManifestTuple = readonly [
  levelNumber: number,
  resolvedSeed: number,
  maskId: string,
  paramsId: string,
  contentHash: string,
  arrowCount: number,
  averageArrowLength: number,
  totalTurnCount: number,
  initialRemovableCount: number,
  peelDepth: number,
  minPeelWidth: number,
  longRangeBlockCount: number,
  minChoiceCount: number,
  averageChoiceCount: number,
  ringMotifCount: number,
  ringEnclosedCellCount: number,
  chainRunCount: number,
  trapCount: number,
];

export interface LevelManifestIndex {
  readonly generatorVersion: number;
  readonly definitionsHash: string;
  readonly levelCount: number;
  readonly packSize: number;
}

export interface LevelPack {
  readonly levels: readonly ManifestTuple[];
}

const tupleLength = 18;

function numberAt(tuple: readonly unknown[], index: number, levelIndex: number): number {
  const value = tuple[index];
  if (typeof value !== 'number') {
    throw new Error(`Manifest level tuple ${levelIndex} has a non-number at index ${index}.`);
  }
  return value;
}

function stringAt(tuple: readonly unknown[], index: number, levelIndex: number): string {
  const value = tuple[index];
  if (typeof value !== 'string') {
    throw new Error(`Manifest level tuple ${levelIndex} has a non-string at index ${index}.`);
  }
  return value;
}

export function encodeManifestEntry(entry: ManifestLevelEntry): ManifestTuple {
  return [
    entry.levelNumber,
    entry.resolvedSeed,
    entry.maskId,
    entry.paramsId,
    entry.contentHash,
    entry.arrowCount,
    entry.averageArrowLength,
    entry.totalTurnCount,
    entry.initialRemovableCount,
    entry.peelDepth,
    entry.minPeelWidth,
    entry.longRangeBlockCount,
    entry.minChoiceCount,
    entry.averageChoiceCount,
    entry.ringMotifCount,
    entry.ringEnclosedCellCount,
    entry.chainRunCount,
    entry.trapCount,
  ];
}

export function decodeManifestEntry(value: unknown, levelIndex: number): ManifestLevelEntry {
  if (!Array.isArray(value) || value.length !== tupleLength) {
    throw new Error(`Manifest level tuple ${levelIndex} must contain ${tupleLength} values.`);
  }
  return {
    levelNumber: numberAt(value, 0, levelIndex),
    resolvedSeed: numberAt(value, 1, levelIndex),
    maskId: stringAt(value, 2, levelIndex),
    paramsId: stringAt(value, 3, levelIndex),
    contentHash: stringAt(value, 4, levelIndex),
    arrowCount: numberAt(value, 5, levelIndex),
    averageArrowLength: numberAt(value, 6, levelIndex),
    totalTurnCount: numberAt(value, 7, levelIndex),
    initialRemovableCount: numberAt(value, 8, levelIndex),
    peelDepth: numberAt(value, 9, levelIndex),
    minPeelWidth: numberAt(value, 10, levelIndex),
    longRangeBlockCount: numberAt(value, 11, levelIndex),
    minChoiceCount: numberAt(value, 12, levelIndex),
    averageChoiceCount: numberAt(value, 13, levelIndex),
    ringMotifCount: numberAt(value, 14, levelIndex),
    ringEnclosedCellCount: numberAt(value, 15, levelIndex),
    chainRunCount: numberAt(value, 16, levelIndex),
    trapCount: numberAt(value, 17, levelIndex),
  };
}

function positiveIntegerAt(candidate: Record<string, unknown>, name: string): number {
  const value = candidate[name];
  if (!Number.isInteger(value) || (value as number) < 1) {
    throw new Error(`Manifest index ${name} must be a positive integer.`);
  }
  return value as number;
}

export function encodeManifestIndex(index: LevelManifestIndex): LevelManifestIndex {
  return decodeManifestIndex(index);
}

export function decodeManifestIndex(value: unknown): LevelManifestIndex {
  if (typeof value !== 'object' || value === null) {
    throw new Error('Manifest index root must be an object.');
  }
  const candidate = value as Record<string, unknown>;
  if (typeof candidate.generatorVersion !== 'number'
    || typeof candidate.definitionsHash !== 'string') {
    throw new Error('Manifest index header is invalid.');
  }
  return {
    generatorVersion: candidate.generatorVersion,
    definitionsHash: candidate.definitionsHash,
    levelCount: positiveIntegerAt(candidate, 'levelCount'),
    packSize: positiveIntegerAt(candidate, 'packSize'),
  };
}

export function encodeLevelPack(entries: readonly ManifestLevelEntry[]): LevelPack {
  return { levels: entries.map(encodeManifestEntry) };
}

export function decodeLevelPack(value: unknown): LevelPack {
  if (typeof value !== 'object' || value === null) {
    throw new Error('Level pack root must be an object.');
  }
  const candidate = value as Record<string, unknown>;
  if (!Array.isArray(candidate.levels)) {
    throw new Error('Level pack levels must be an array.');
  }
  return {
    levels: candidate.levels.map((tuple, index) =>
      encodeManifestEntry(decodeManifestEntry(tuple, index))),
  };
}

export function manifestHeaderDifferences(
  manifest: Pick<LevelManifestIndex, 'generatorVersion' | 'definitionsHash'>,
  currentDefinitionsHash: string,
): readonly string[] {
  const differences: string[] = [];
  if (manifest.generatorVersion !== generatorVersion) {
    differences.push(
      `generatorVersion: manifest=${manifest.generatorVersion}, current=${generatorVersion}`,
    );
  }
  if (manifest.definitionsHash !== currentDefinitionsHash) {
    differences.push(
      `definitionsHash: manifest=${manifest.definitionsHash}, current=${currentDefinitionsHash}`,
    );
  }
  return differences;
}

export function assertManifestHeader(
  manifest: Pick<LevelManifestIndex, 'generatorVersion' | 'definitionsHash'>,
  currentDefinitionsHash: string,
): void {
  const differences = manifestHeaderDifferences(manifest, currentDefinitionsHash);
  if (differences.length > 0) {
    throw new Error(`Manifest header mismatch: ${differences.join('; ')}`);
  }
}
